import { useMemo } from 'react';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { MeTeam } from '@scio3/shared-types';
import { ROOT_TEAM_ID } from '@scio3/shared-types';
import { useFavoritesStore } from '@/stores/favorites-store';
// The All-view flag + its scope sentinel live with the feature (one place to flip, per Ted's
// 2026-08-26 decision), so the store reads them rather than redeclaring them.
import { ALL_TEAMS_SCOPE, SHOW_ALL_VIEW } from '@/features/group-security/constants';

interface TeamState {
  teams: MeTeam[];
  activeTeamId: string;
  /** Whether the active team is the root/default team */
  isDefaultTeam: boolean;
  /**
   * List SCOPE (Group Security §1, T901): `ALL_TEAMS_SCOPE` or a teamId.
   *
   * Separate from `activeTeamId` on purpose — `activeTeamId` is the AUTH team (the top-level
   * `teamId` GraphQL arg, which must always be a real teamspace the user belongs to), while this is
   * the optional `query.teamId` FILTER on top of it. Picking a team sets both; picking All changes
   * only this one.
   *
   * AV-1: the default is the current team, never All — `clearTeams()` (logout) resets it, so a
   * fresh login always lands on currentTeam.
   */
  teamFilter: string;

  setTeams: (teams: MeTeam[]) => void;
  setActiveTeam: (teamId: string) => void;
  setTeamFilter: (teamFilter: string) => void;
  getActiveTeam: () => MeTeam | undefined;
  clearTeams: () => void;
}

export const useTeamStore = create<TeamState>()(
  persist(
    (set, get) => ({
      teams: [],
      activeTeamId: '',
      isDefaultTeam: true,
      teamFilter: '',

      setTeams: (teams) => {
        const current = get().activeTeamId;
        const isValid = current && teams.some((t) => t._id === current);
        const newTeamId = isValid ? current : (teams[0]?._id ?? '');
        // Reconcile a stale persisted filter: a teamId that is no longer in the user's reach falls
        // back to the resolved active team. `ALL_TEAMS_SCOPE` is preserved — setTeams also runs on
        // an in-session ME refetch, so resetting it here would silently kick the user out of All.
        const filter = get().teamFilter;
        const isFilterValid =
          filter === ALL_TEAMS_SCOPE || (!!filter && teams.some((t) => t._id === filter));
        set({
          teams,
          ...(isValid ? {} : { activeTeamId: newTeamId }),
          isDefaultTeam: !newTeamId || newTeamId === ROOT_TEAM_ID,
          ...(isFilterValid ? {} : { teamFilter: newTeamId }),
        });
      },
      setActiveTeam: (teamId) => {
        // Favorites are per-(user, team) — wipe on an actual team change so the previous team's
        // stars don't linger until the new team's favorites query reconciles.
        if (teamId !== get().activeTeamId) {
          useFavoritesStore.getState().reset();
        }
        set({
          activeTeamId: teamId,
          isDefaultTeam: !teamId || teamId === ROOT_TEAM_ID,
          // Picking a teamspace leaves the All scope and filters to that team.
          teamFilter: teamId,
        });
      },
      // Entering All keeps `activeTeamId` (the auth team) untouched — see `teamFilter` above.
      setTeamFilter: (teamFilter) => set({ teamFilter }),
      getActiveTeam: () => get().teams.find((t) => t._id === get().activeTeamId),
      clearTeams: () => set({ teams: [], activeTeamId: '', teamFilter: '' }),
    }),
    { name: 'scio3-team' },
  ),
);

type TeamScopeState = Pick<TeamState, 'teams' | 'activeTeamId' | 'teamFilter'>;

/**
 * Is the [ALL] view offered to this user at all? Flag ON **and** reach > 1 teamspace — with a
 * single teamspace, All and that teamspace are the same list, so the entry is noise. Reach-based,
 * never role-based (Ted, 2026-08-26: no role gate).
 */
export function selectIsAllViewAvailable(state: TeamScopeState): boolean {
  return SHOW_ALL_VIEW && state.teams.length > 1;
}

/** Is the user currently scoped to All? False whenever the view isn't available to them. */
export function selectIsAllTeamsScope(state: TeamScopeState): boolean {
  return selectIsAllViewAvailable(state) && state.teamFilter === ALL_TEAMS_SCOPE;
}

/**
 * The `query.teamId` filter a ROOT-level list should send. THREE explicit states — this selector is
 * the single decision point every list hook reads (T903):
 *
 *  1. All scope        ⇒ `ALL_TEAMS_SCOPE` — the sentinel is sent EXPLICITLY (Ted, 2026-08-26:
 *     "we should create filter teamId = 'all'"). The backend normalizes it away before building the
 *     query, so the reach is the same full cross-team reach as omitting the field. Sending it
 *     beats omitting it because "no filter" is then unambiguous: a real teamId AND-scopes the list
 *     (that narrowing is the whole point of the picker), so "field absent" can keep meaning
 *     "back-compat client that never heard of the scope" instead of doubling as "All".
 *  2. Teamspace scope  ⇒ the auth team's id — narrows the list to rows OWNED by that teamspace,
 *     the same value the Workspace column shows, so filter and column always agree.
 *  3. All view not available (single-teamspace reach) ⇒ `undefined`, so the field is OMITTED and
 *     those accounts keep sending byte-identical variables: no new Apollo cache key, no extra
 *     fetch, no behavior change from before the All view existed.
 *
 * `ALL_TEAMS_SCOPE` must never be used as the top-level `teamId` GraphQL ARG (the AUTH team, which
 * is membership-validated) — only ever inside the `query` / `input` FILTER object.
 */
export function selectListScopeTeamId(state: TeamScopeState): string | undefined {
  if (!selectIsAllViewAvailable(state)) return undefined;
  return state.teamFilter === ALL_TEAMS_SCOPE ? ALL_TEAMS_SCOPE : state.activeTeamId;
}

/**
 * EVERY teamspace this user belongs to — the frontend's equivalent of the backend's reach.
 *
 * This is the correct `teamIds` argument for `resolveFolderAccess`. Passing only `activeTeamId`
 * there is a BUG: the backend authorizes a folder against all of my teamspaces (v4 D2 — my
 * teamspaces ∪ shared-to-me ∪ Everyone-in-account), so a folder living in another teamspace I am a
 * member of is served by the server and would then be refused by the client. That mismatch is what
 * made clicking a cross-teamspace folder in All view bounce to Home with "You do not have access to
 * this folder" (#GS-allview). Group grants come from `me.groupIds`, which the resolver takes
 * separately — this covers the teamspace-membership half only.
 *
 * Returns the store's own `teams` mapping; callers that need referential stability across renders
 * should use `useMyTeamIds()` below rather than calling this in a zustand selector.
 */
export function selectMyTeamIds(state: TeamScopeState): string[] {
  return state.teams.map((t) => t._id);
}

/**
 * Referentially-stable `selectMyTeamIds` for use in render / `useMemo` deps.
 *
 * `teams` is a stable reference from the store, so the mapped array is only rebuilt when the team
 * list actually changes. Selecting the mapped array directly from zustand would allocate a new
 * array on every store read and re-render forever.
 */
export function useMyTeamIds(): string[] {
  const teams = useTeamStore((s) => s.teams);
  return useMemo(() => teams.map((t) => t._id), [teams]);
}
